import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { api, SitePlanBlock, SitePlanData } from '../../services/api';

interface BlockIdentificationDialogProps {
  open: boolean;
  onClose: () => void;
  projects: string[];
  initialProjectName?: string;
  resolveProjectIdByName: (projectName: string) => Promise<string | null>;
  fetchSitePlan: (projectName: string, projectId?: string | null) => Promise<{ sitePlan: SitePlanData | null; projectId: string }>;
  onCoordinatesSaved?: (projectName: string) => void | Promise<void>;
}

const BlockIdentificationDialog: React.FC<BlockIdentificationDialogProps> = ({
  open,
  onClose,
  projects,
  initialProjectName,
  resolveProjectIdByName,
  fetchSitePlan,
  onCoordinatesSaved,
}) => {
  const [selectedProjectForBlockId, setSelectedProjectForBlockId] = useState<string>('');
  const [blockIdSitePlanData, setBlockIdSitePlanData] = useState<SitePlanData | null>(null);
  const [blockIdLoading, setBlockIdLoading] = useState<boolean>(false);
  const [blockIdBlockNumbers, setBlockIdBlockNumbers] = useState<string[]>([]);
  const [blockIdStackNumbers, setBlockIdStackNumbers] = useState<string[]>([]);
  const [blockIdCoordinates, setBlockIdCoordinates] = useState<Map<string, SitePlanBlock>>(new Map());
  const [clickedPoint, setClickedPoint] = useState<{ x: number; y: number } | null>(null);
  const [blockSelectPopoverAnchor, setBlockSelectPopoverAnchor] = useState<{ x: number; y: number } | null>(null);
  const [coordinateType, setCoordinateType] = useState<'block' | 'stack'>('block');
  const [selectedStack, setSelectedStack] = useState<string>('');
  const [selectedFacing, setSelectedFacing] = useState<string>('');
  const [selectedBlockForStack, setSelectedBlockForStack] = useState<string>('');
  const [showPlottedPoints, setShowPlottedPoints] = useState<boolean>(true);
  const [draggedBlock, setDraggedBlock] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [sitePlanUploadLoading, setSitePlanUploadLoading] = useState<boolean>(false);
  const [replaceSitePlanPromptOpen, setReplaceSitePlanPromptOpen] = useState<boolean>(false);
  const [pendingSitePlanFile, setPendingSitePlanFile] = useState<File | null>(null);
  const sitePlanContainerRef = useRef<HTMLDivElement | null>(null);

  const resetLocalState = useCallback(() => {
    setSelectedProjectForBlockId('');
    setBlockIdSitePlanData(null);
    setBlockIdLoading(false);
    setBlockIdBlockNumbers([]);
    setBlockIdStackNumbers([]);
    setBlockIdCoordinates(new Map());
    setClickedPoint(null);
    setBlockSelectPopoverAnchor(null);
    setCoordinateType('block');
    setSelectedStack('');
    setSelectedFacing('');
    setSelectedBlockForStack('');
    setShowPlottedPoints(true);
    setDraggedBlock(null);
    setDragOffset(null);
    setSitePlanUploadLoading(false);
    setReplaceSitePlanPromptOpen(false);
    setPendingSitePlanFile(null);
  }, []);

  const handleProjectSelectForBlockId = useCallback(async (projectName: string) => {
    setSelectedProjectForBlockId(projectName);
    setBlockIdLoading(true);

    try {
      const projectId = await resolveProjectIdByName(projectName);
      if (!projectId) {
        throw new Error(`Unable to resolve project id for ${projectName}`);
      }

      const { sitePlan } = await fetchSitePlan(projectName, projectId);
      setBlockIdSitePlanData(sitePlan);

      try {
        const stacksData = await api.getStacks(projectId);
        setBlockIdStackNumbers(stacksData.stacks);
      } catch {
        setBlockIdStackNumbers([]);
      }

      try {
        const savedCoords = await api.getBlockCoordinates(
          projectId,
          sitePlan?.projectMediaId ?? undefined,
          sitePlan?.projectPickerMediaId ?? undefined,
        );
        const coordsMap = new Map<string, SitePlanBlock>();
        savedCoords.coordinates.forEach((coord) => {
          let key: string;
          if (coord.blockNumber && !coord.stack) {
            key = `block:${coord.blockNumber}`;
          } else if (coord.blockNumber && coord.stack && coord.facing) {
            key = `block:${coord.blockNumber}:stack:${coord.stack}:${coord.facing}`;
          } else if (coord.stack && coord.facing) {
            key = `stack:${coord.stack}:${coord.facing}`;
          } else {
            return;
          }

          coordsMap.set(key, {
            blockNumber: coord.blockNumber,
            stack: coord.stack,
            facing: coord.facing,
            x: coord.x,
            y: coord.y,
            width: coord.width,
            height: coord.height,
          });
        });
        setBlockIdCoordinates(coordsMap);
      } catch {
        setBlockIdCoordinates(new Map());
      }

      if (sitePlan) {
        const blockNumbers = sitePlan.blockNumbers && sitePlan.blockNumbers.length > 0
          ? sitePlan.blockNumbers
          : sitePlan.blocks?.map((b) => b.blockNumber || '') || [];
        setBlockIdBlockNumbers(blockNumbers.filter((b) => b !== ''));
      }
    } catch (err) {
      console.error('Failed to load site plan for block identification:', err);
      setBlockIdSitePlanData(null);
      setBlockIdBlockNumbers([]);
      setBlockIdCoordinates(new Map());
    } finally {
      setBlockIdLoading(false);
    }
  }, [fetchSitePlan, resolveProjectIdByName]);

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }

    const startingProject = (initialProjectName || '').trim();
    if (!startingProject) {
      setSelectedProjectForBlockId('');
      return;
    }

    void handleProjectSelectForBlockId(startingProject);
  }, [open, initialProjectName, handleProjectSelectForBlockId, resetLocalState]);

  const handleSitePlanImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggedBlock || !blockIdSitePlanData) return;

    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    if (showPlottedPoints) {
      const clickedOnBlock = Array.from(blockIdCoordinates.values()).find((block) => (
        x >= block.x && x <= block.x + block.width && y >= block.y && y <= block.y + block.height
      ));

      if (clickedOnBlock) {
        return;
      }
    }

    setBlockSelectPopoverAnchor({ x: event.clientX, y: event.clientY });
    setClickedPoint({ x, y });
  };

  const handleBlockSelectFromPopover = (blockNumber: string) => {
    if (!clickedPoint || !blockIdSitePlanData) return;

    const newCoord: SitePlanBlock = {
      blockNumber,
      x: Math.max(0, Math.min(100, clickedPoint.x - 4)),
      y: Math.max(0, Math.min(100, clickedPoint.y - 4)),
      width: 8,
      height: 8,
    };

    const key = `block:${blockNumber}`;
    setBlockIdCoordinates((prev) => {
      const next = new Map(prev);
      next.set(key, newCoord);
      return next;
    });

    setBlockSelectPopoverAnchor(null);
    setClickedPoint(null);
  };

  const handleStackSelectFromPopover = () => {
    if (!clickedPoint || !blockIdSitePlanData || !selectedStack || !selectedFacing) return;

    const newCoord: SitePlanBlock = {
      blockNumber: selectedBlockForStack || undefined,
      stack: selectedStack,
      facing: selectedFacing,
      x: Math.max(0, Math.min(100, clickedPoint.x - 4)),
      y: Math.max(0, Math.min(100, clickedPoint.y - 4)),
      width: 8,
      height: 8,
    };

    const key = selectedBlockForStack
      ? `block:${selectedBlockForStack}:stack:${selectedStack}:${selectedFacing}`
      : `stack:${selectedStack}:${selectedFacing}`;

    setBlockIdCoordinates((prev) => {
      const next = new Map(prev);
      next.set(key, newCoord);
      return next;
    });

    setBlockSelectPopoverAnchor(null);
    setClickedPoint(null);
    setSelectedStack('');
    setSelectedFacing('');
    setSelectedBlockForStack('');
  };

  const handleDeleteBlock = (key: string) => {
    setBlockIdCoordinates((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const handleBlockMouseDown = (event: React.MouseEvent<HTMLDivElement>, key: string) => {
    event.stopPropagation();
    const block = blockIdCoordinates.get(key);
    const container = sitePlanContainerRef.current;
    if (!block || !container) return;

    const containerRect = container.getBoundingClientRect();
    const offsetX = ((event.clientX - containerRect.left) / containerRect.width) * 100 - block.x;
    const offsetY = ((event.clientY - containerRect.top) / containerRect.height) * 100 - block.y;

    setDraggedBlock(key);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleBlockMouseMove = useCallback((event: MouseEvent) => {
    if (!draggedBlock || !dragOffset || !blockIdSitePlanData || !sitePlanContainerRef.current) return;

    const rect = sitePlanContainerRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
    const y = ((event.clientY - rect.top) / rect.height) * 100 - dragOffset.y;

    setBlockIdCoordinates((prev) => {
      const next = new Map(prev);
      const block = next.get(draggedBlock);
      if (block) {
        next.set(draggedBlock, {
          ...block,
          x: Math.max(0, Math.min(100 - block.width, x)),
          y: Math.max(0, Math.min(100 - block.height, y)),
        });
      }
      return next;
    });
  }, [draggedBlock, dragOffset, blockIdSitePlanData]);

  const handleBlockMouseUp = useCallback(() => {
    setDraggedBlock(null);
    setDragOffset(null);
  }, []);

  useEffect(() => {
    if (!draggedBlock) return undefined;

    document.addEventListener('mousemove', handleBlockMouseMove);
    document.addEventListener('mouseup', handleBlockMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleBlockMouseMove);
      document.removeEventListener('mouseup', handleBlockMouseUp);
    };
  }, [draggedBlock, handleBlockMouseMove, handleBlockMouseUp]);

  const handleSaveBlockCoordinates = useCallback(async () => {
    if (!selectedProjectForBlockId || blockIdCoordinates.size === 0) return;

    try {
      const coordinates = Array.from(blockIdCoordinates.values()).filter((coord) => {
        const hasBlock = Boolean(coord.blockNumber);
        const hasStack = Boolean(coord.stack) && Boolean(coord.facing);
        return hasBlock || hasStack;
      });

      if (coordinates.length === 0) {
        alert('No valid coordinates to save.');
        return;
      }

      const projectId = await resolveProjectIdByName(selectedProjectForBlockId);
      if (!projectId) {
        throw new Error(`Unable to resolve project id for ${selectedProjectForBlockId}`);
      }

      await api.saveBlockCoordinates(
        projectId,
        coordinates,
        blockIdSitePlanData?.projectMediaId ?? undefined,
        blockIdSitePlanData?.projectPickerMediaId ?? undefined,
      );
      try {
        await Promise.resolve(onCoordinatesSaved?.(selectedProjectForBlockId));
      } catch (callbackError) {
        console.error('Failed to refresh site plan after saving coordinates:', callbackError);
      }
      alert(`Successfully saved ${coordinates.length} coordinate(s)!`);
      onClose();
    } catch (err) {
      console.error('Failed to save coordinates:', err);
      alert('Failed to save coordinates. Please try again.');
    }
  }, [blockIdCoordinates, blockIdSitePlanData?.projectMediaId, blockIdSitePlanData?.projectPickerMediaId, onClose, onCoordinatesSaved, resolveProjectIdByName, selectedProjectForBlockId]);

  const performSitePlanUpload = useCallback(async (file: File) => {
    try {
      setSitePlanUploadLoading(true);
      const projectId = await resolveProjectIdByName(selectedProjectForBlockId);
      if (!projectId) {
        throw new Error(`Unable to resolve project id for ${selectedProjectForBlockId}`);
      }

      const imageDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(file);
      });

      if (!imageDataUrl) {
        throw new Error('Failed to read image file');
      }

      await api.uploadProjectPickerSitePlan(projectId, imageDataUrl);
      await handleProjectSelectForBlockId(selectedProjectForBlockId);
    } catch (err) {
      console.error('Failed to upload site plan:', err);
      alert(err instanceof Error ? err.message : 'Failed to upload site plan. Please try again.');
    } finally {
      setSitePlanUploadLoading(false);
    }
  }, [handleProjectSelectForBlockId, resolveProjectIdByName, selectedProjectForBlockId]);

  const handleSitePlanUpload = useCallback(async (file: File | null) => {
    if (!file || !selectedProjectForBlockId) return;

    if (!file.type.startsWith('image/')) {
      alert('Only image uploads are supported for site plan.');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      alert('Site plan image is too large. Maximum size is 8MB.');
      return;
    }

    if (blockIdCoordinates.size > 0) {
      setPendingSitePlanFile(file);
      setReplaceSitePlanPromptOpen(true);
      return;
    }

    await performSitePlanUpload(file);
  }, [blockIdCoordinates.size, performSitePlanUpload, selectedProjectForBlockId]);

  const handleConfirmSitePlanReplace = useCallback(() => {
    const file = pendingSitePlanFile;
    setReplaceSitePlanPromptOpen(false);
    setPendingSitePlanFile(null);
    if (!file) return;
    void performSitePlanUpload(file);
  }, [pendingSitePlanFile, performSitePlanUpload]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Block Identification Module</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                options={projects}
                value={selectedProjectForBlockId || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setSelectedProjectForBlockId('');
                    setBlockIdSitePlanData(null);
                    setBlockIdBlockNumbers([]);
                    setBlockIdCoordinates(new Map());
                    return;
                  }
                  void handleProjectSelectForBlockId(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Project" variant="outlined" />
                )}
              />
            </FormControl>

            {selectedProjectForBlockId && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    disabled={sitePlanUploadLoading}
                  >
                    {sitePlanUploadLoading ? 'Uploading...' : 'Upload Site Plan'}
                    <input
                      hidden
                      accept="image/png,image/jpeg,image/webp"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        void handleSitePlanUpload(file);
                        e.currentTarget.value = '';
                      }}
                    />
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Click anywhere on the site plan to add a block or stack. Drag existing items to move them. Click the delete icon to remove an item.
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={showPlottedPoints}
                        onChange={(e) => setShowPlottedPoints(e.target.checked)}
                        color="primary"
                      />
                    )}
                    label={(
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Show Plotted Points ({blockIdCoordinates.size})
                      </Typography>
                    )}
                  />
                </Box>
              </>
            )}
          </Box>

          {blockIdLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : blockIdSitePlanData ? (
            <Box
              ref={sitePlanContainerRef}
              sx={{ position: 'relative', width: '100%' }}
              data-site-plan-container
              onClick={handleSitePlanImageClick}
            >
              <Box
                component="img"
                src={blockIdSitePlanData.imageUrl}
                alt="Site Plan"
                sx={{
                  width: '100%',
                  height: 'auto',
                  cursor: 'crosshair',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
              {showPlottedPoints && Array.from(blockIdCoordinates.entries()).map(([key, coord]) => {
                const isBlock = Boolean(coord.blockNumber) && !coord.stack;
                const label = isBlock
                  ? coord.blockNumber!
                  : coord.blockNumber
                  ? `${coord.blockNumber} - ${coord.stack} (${coord.facing})`
                  : `${coord.stack} (${coord.facing})`;

                return (
                  <Box
                    key={key}
                    onMouseDown={(e) => handleBlockMouseDown(e, key)}
                    sx={{
                      position: 'absolute',
                      left: `${coord.x}%`,
                      top: `${coord.y}%`,
                      width: `${coord.width}%`,
                      height: `${coord.height}%`,
                      border: '2px solid',
                      borderColor: draggedBlock === key ? 'error.main' : (isBlock ? 'primary.main' : 'secondary.main'),
                      bgcolor: draggedBlock === key
                        ? 'rgba(var(--color-danger-rgb), 0.3)'
                        : (isBlock ? 'rgba(var(--color-info-rgb), 0.2)' : 'rgba(var(--color-success-rgb), 0.2)'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: draggedBlock === key ? 'grabbing' : 'grab',
                      pointerEvents: 'auto',
                      '&:hover': {
                        bgcolor: draggedBlock === key
                          ? 'rgba(var(--color-danger-rgb), 0.4)'
                          : (isBlock ? 'rgba(var(--color-info-rgb), 0.3)' : 'rgba(var(--color-success-rgb), 0.3)'),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: isBlock ? 'primary.main' : 'secondary.main',
                          color: 'var(--color-text-strong)',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontWeight: 'bold',
                          pointerEvents: 'none',
                          fontSize: '0.7rem',
                        }}
                      >
                        {label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBlock(key);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'var(--color-text-strong)',
                          width: 20,
                          height: 20,
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Please select a project to load the site plan
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveBlockCoordinates}
            disabled={blockIdCoordinates.size === 0 || !selectedProjectForBlockId}
          >
            Save Coordinates ({blockIdCoordinates.size})
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={blockSelectPopoverAnchor !== null}
        onClose={() => {
          setBlockSelectPopoverAnchor(null);
          setClickedPoint(null);
          setSelectedStack('');
          setSelectedFacing('');
          setSelectedBlockForStack('');
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          blockSelectPopoverAnchor
            ? { top: blockSelectPopoverAnchor.y, left: blockSelectPopoverAnchor.x }
            : undefined
        }
      >
        <Box sx={{ minWidth: 300 }}>
          <Tabs
            value={coordinateType}
            onChange={(_, newValue) => { setCoordinateType(newValue); if (newValue === 'block') setSelectedBlockForStack(''); }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Block" value="block" />
            <Tab label="Stack" value="stack" />
          </Tabs>

          {coordinateType === 'block' ? (
            <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Block Number:
              </Typography>
              {blockIdBlockNumbers.map((blockNum) => {
                const key = `block:${blockNum}`;
                return (
                  <Button
                    key={blockNum}
                    fullWidth
                    variant={blockIdCoordinates.has(key) ? 'outlined' : 'contained'}
                    onClick={() => handleBlockSelectFromPopover(blockNum)}
                    sx={{ mb: 0.5 }}
                  >
                    {blockNum}
                    {blockIdCoordinates.has(key) && ' (Update)'}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Stack and Facing:
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Block (optional)</InputLabel>
                <Select
                  value={selectedBlockForStack}
                  onChange={(e) => setSelectedBlockForStack(e.target.value)}
                  label="Block (optional)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {blockIdBlockNumbers.map((block) => (
                    <MenuItem key={block} value={block}>
                      {block}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Stack</InputLabel>
                <Select
                  value={selectedStack}
                  onChange={(e) => setSelectedStack(e.target.value)}
                  label="Stack"
                >
                  {blockIdStackNumbers.map((stack) => (
                    <MenuItem key={stack} value={stack}>
                      {stack}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Facing</InputLabel>
                <Select
                  value={selectedFacing}
                  onChange={(e) => setSelectedFacing(e.target.value)}
                  label="Facing"
                >
                  <MenuItem value="North">North</MenuItem>
                  <MenuItem value="NorthEast">North East</MenuItem>
                  <MenuItem value="East">East</MenuItem>
                  <MenuItem value="SouthEast">South East</MenuItem>
                  <MenuItem value="South">South</MenuItem>
                  <MenuItem value="SouthWest">South West</MenuItem>
                  <MenuItem value="West">West</MenuItem>
                  <MenuItem value="NorthWest">North West</MenuItem>
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="contained"
                disabled={!selectedStack || !selectedFacing}
                onClick={handleStackSelectFromPopover}
              >
                Add Stack
              </Button>
            </Box>
          )}
        </Box>
      </Popover>

      <Dialog
        open={replaceSitePlanPromptOpen}
        onClose={() => {
          if (sitePlanUploadLoading) return;
          setReplaceSitePlanPromptOpen(false);
          setPendingSitePlanFile(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Replace Site Plan?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Uploading a new site plan will remove the existing plotted coordinates for this project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReplaceSitePlanPromptOpen(false);
              setPendingSitePlanFile(null);
            }}
            disabled={sitePlanUploadLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSitePlanReplace}
            variant="contained"
            color="warning"
            disabled={sitePlanUploadLoading}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BlockIdentificationDialog;
